import {
  isAppError,
  normalizeError,
  resolveErrorCopy,
} from '@core/errors/index';
import {
  buildErrorSummary,
  describeErrorSource,
  isMeaningfulStackTrace,
} from '@core/errors/describeError';
import { getTraceContext, newSpanId } from '@services/audit/traceContext';
import { logAuditEvent } from '@services/audit/AuditService';
import { reportError } from '@services/firebase/CrashReportingService';

export interface PresentErrorOptions {
  t: Record<string, string>;
  push?: (fields: {
    cat: string;
    sev: string;
    title: string;
    body: string;
    urgent?: boolean;
  }) => void;
  context?: Record<string, unknown>;
  report?: boolean;
  forceToast?: boolean;
}

/**
 * Log structured error + optional user toast. Returns copy for inline UI.
 */
export function presentAppError(error: unknown, opts: PresentErrorOptions) {
  const { traceId } = getTraceContext();
  const appErr = isAppError(error)
    ? error
    : normalizeError(error, { traceId, spanId: newSpanId('error') });

  logErrorToConsole(appErr, opts.context);

  if (opts.report !== false && appErr.severity === 'error' && appErr.presentation !== 'silent') {
    reportAppError(appErr, opts.context);
  }

  const copy = resolveErrorCopy(appErr, opts?.t);
  const showToast = opts.forceToast || appErr.presentation === 'toast';
  // Overlay alerts are reserved for security / forced / severe platform failures.
  // Everything else still lands in the notification inbox when push is called.
  const urgent = Boolean(
    opts.forceToast
    || appErr.category === 'permission'
    || (
      appErr.severity === 'error'
      && (appErr.category === 'internal' || appErr.category === 'config')
    ),
  );

  if (showToast && opts.push) {
    opts.push({
      cat: 'system',
      sev: appErr.severity === 'error' ? 'danger' : appErr.severity,
      title: copy.title,
      body: copy.hint ?? '',
      urgent,
    });
  }

  return { appErr, copy };
}

export function reportAppError(error: unknown, context: Record<string, unknown> = {}) {
  const appErr = isAppError(error) ? error : normalizeError(error);
  // Avoid cascading audit/callable failures while the device is offline.
  if (
    appErr.category === 'offline'
    || appErr.category === 'unavailable'
    || (typeof navigator !== 'undefined' && navigator.onLine === false)
  ) {
    return Promise.resolve();
  }
  const { traceId, requestId } = getTraceContext();
  const source = describeErrorSource(error);
  const feature = typeof context.feature === 'string' ? context.feature : undefined;
  const operation = typeof appErr.details.operation === 'string'
    ? appErr.details.operation
    : typeof context.operation === 'string'
      ? context.operation
      : undefined;
  const sourceCode = String(
    appErr.details.sourceCode
    ?? appErr.details.firebaseCode
    ?? source.sourceCode
    ?? '',
  );
  const sourceMessage = String(
    appErr.details.sourceMessage
    ?? appErr.details.causeMessage
    ?? source.sourceMessage
    ?? appErr.message
    ?? '',
  );
  const summary = buildErrorSummary({
    code: appErr.code,
    sourceCode,
    sourceMessage,
    feature,
    operation,
  });
  const spanId = appErr.spanId ?? newSpanId('error');

  return logAuditEvent({
    action: 'error.app',
    severity: appErr.severity === 'info' ? 'info' : 'error',
    spanId,
    // Top-level msg so audit UI shows the real cause, not only "error.app · code".
    msg: summary,
    payload: {
      summary,
      code: appErr.code,
      category: appErr.category,
      userMessageKey: appErr.userMessageKey,
      developerMessage: appErr.message,
      sourceCode,
      sourceName: source.sourceName || appErr.details.sourceName,
      sourceMessage,
      feature,
      operation,
      traceId: appErr.traceId ?? traceId,
      requestId,
      details: appErr.details,
      ...context,
      stack: resolveErrorStack(appErr, source),
    },
    resource: { type: 'error', id: appErr.code, path: feature || operation || '' },
  }).catch(() => {
    reportError(appErr, { ...context, summary });
  });
}

function resolveErrorStack(
  appErr: ReturnType<typeof normalizeError>,
  source: ReturnType<typeof describeErrorSource>,
): string | undefined {
  const fromDetails = typeof appErr.details.sourceStack === 'string'
    ? appErr.details.sourceStack
    : '';
  const fromSource = source.sourceStack ?? '';
  const fromCause = appErr.cause instanceof Error ? (appErr.cause.stack ?? '') : '';
  const stack = fromDetails || fromSource || fromCause;
  return isMeaningfulStackTrace(stack) ? stack.trim() : undefined;
}

function logErrorToConsole(appErr: ReturnType<typeof normalizeError>, context?: Record<string, unknown>) {
  if (appErr.presentation === 'silent' || appErr.severity === 'info') return;
  const source = describeErrorSource(appErr);
  const summary = buildErrorSummary({
    code: appErr.code,
    sourceCode: String(appErr.details.sourceCode ?? appErr.details.firebaseCode ?? ''),
    sourceMessage: String(appErr.details.sourceMessage ?? appErr.details.causeMessage ?? appErr.message ?? ''),
    feature: typeof context?.feature === 'string' ? context.feature : undefined,
    operation: typeof appErr.details.operation === 'string' ? appErr.details.operation : undefined,
  });
  const method = appErr.severity === 'error' ? 'error' : 'warn';
  console[method](`[AppError] ${summary}`, {
    code: appErr.code,
    category: appErr.category,
    details: appErr.details,
    stack: resolveErrorStack(appErr, source),
    context,
  });
}
